import { Component, Input, OnDestroy, ViewChild } from '@angular/core';
import { fabric } from 'fabric';
fabric.textureSize = 6000;
import { ApiService } from '../../shared/api.service';
import { ImagesService } from '../../shared/images.service';

@Component({
    selector: 'cropper',
    templateUrl: 'cropper.component.html'
})
export class CropperComponent implements OnDestroy {
    cropped = false;
    initialCrop;
    imageWidth;
    imageHeight;
    loading = false;
    lastBrightness = 0;
    lastContrast = 0;
    showCropper = true;
    minScale = 1.0;

    canvas;
    canvasImage;
    canvasError = '';
    canvasMouseDownRef;
    canvasAfterRenderRef;
    canvasImageMoveRef;
    canvasImageMousewheelRef;

    private _canEdit = false;
    private _image;
    private _size = {
        value: 'full',
        label: 'Desktop',
        fixed: false,
        width: 600,
        height: 0
    };

    @Input('canEdit')
    set canEdit(canEdit) {
        if( this.canvasImage ) {
            this.canvasImage.set({
                lockMovementX: !canEdit,
                lockMovementY: !canEdit,
                hoverCursor: !canEdit ? 'default' : 'move'
            });
            if( canEdit === true ) {
                this.canvasImage.on('moving', this.canvasImageMoveRef);
                this.canvasImage.on('mousewheel', this.canvasImageMousewheelRef);
            } else {
                this.canvasImage.off('moving', this.canvasImageMoveRef);
                this.canvasImage.off('mousewheel', this.canvasImageMousewheelRef);
            }
        }
        this._canEdit = canEdit;
    }
    get canEdit() {
        return this._canEdit;
    }
    @Input('image')
    set image(image) {
        if( image ) {
            this._image = image;
            if( this.canvas ) {
                this.cropped = false;
                this.canvas.dispose();
                this.ngAfterViewInit();
            }
        }
    }
    get image() {
        return this._image;
    }
    @Input('size')
    set size(size) {
        this._size = size;
    }
    get size() {
        return this._size;
    }

    @ViewChild('canvasEl') canvasEl;
    @ViewChild('canvasWrap') canvasWrap;

    constructor(private api: ApiService, private imageService: ImagesService) {
        this.canvasAfterRenderRef = this.canvasAfterRender.bind(this);
        this.canvasMouseDownRef = this.canvasMouseDown.bind(this);
        this.canvasImageMoveRef = this.canvasImageMove.bind(this);
        this.canvasImageMousewheelRef = this.canvasImageMousewheel.bind(this);
    }
    ngAfterViewInit() {
        const image = this.getImage();
        if( !image ) {
            this.showCropper = false;
            return;
        }
        if( this.image.crop ) {
            this.lastBrightness = this.image.crop.b;
            this.lastContrast = this.image.crop.c;
        }
        this.initCanvas();
    }
    /*
    *   get the image based on size
    */
    getImage() {
        const size = this.size.value;
        return this.image.sizes[size];
    }
    /*
    *   sets the canvas zoom based on the width of the div
    */
    setCanvasZoom() {
        const el = this.canvasWrap.nativeElement;
        const canvas = this.canvas;
        const bounds = el.getBoundingClientRect();
        let widthRatio = 1;
        let heightRatio = 1;
        const maxWidth = bounds.width;
        const maxHeight = 400;
        if( canvas.width > maxWidth ) {
            widthRatio = maxWidth / canvas.width;
        }
        if( canvas.height > maxHeight ) {
            heightRatio = maxHeight / canvas.height;
        }
        const zoom = Math.min(widthRatio, heightRatio);
        const width = canvas.width * zoom;
        const height = canvas.height * zoom;
        canvas.setZoom(zoom);
        el.setAttribute('style', 'height: ' + height + 'px; width: ' + width + 'px');
    }
    /*
    *   canvas setup
    *   add first item to history
    */
    initCanvas() {
        const image = this.getImage();
        if( !this.canvasEl || !image ) {
            this.showCropper = false;
            return;
        }
        this.loading = true;
        const imageEl = new Image();
        imageEl.onload = () => {
            this.canvas = new fabric.Canvas(this.canvasEl.nativeElement);
            this.canvas.on('after:render', this.canvasAfterRenderRef);
            let canvasWidth = image.width;
            let canvasHeight = image.height;
            const crop = image.crop;
            if( crop ) {
                canvasWidth = crop.width;
                canvasHeight = crop.height;
                this.initialCrop = crop;
            }
            this.canvas.setWidth(canvasWidth);
            this.canvas.setHeight(canvasHeight);
            this.imageWidth = canvasWidth;
            this.imageHeight = canvasHeight;

            const currentWidth = imageEl.naturalWidth;
            const currentHeight = imageEl.naturalHeight;
            const minScale = Math.max( canvasWidth / currentWidth, canvasHeight / currentHeight );
            this.minScale = minScale;
            let scale = minScale;
            let newWidth = currentWidth * scale;
            let newHeight = currentHeight * scale;
            let diffX = canvasWidth - newWidth;
            let diffY = canvasHeight - newHeight;
            let left = diffX / 2;
            let top = diffY / 2;
            if( crop ) {
                scale = crop.zoom ? crop.zoom : scale;
                top = typeof crop.top !== 'undefined' ? crop.top * -1 : top;
                left = typeof crop.left !== 'undefined' ? crop.left * -1 : left;
            }
            const canEdit = this.canEdit;
            this.canvasImage = new fabric.Image(imageEl, {
                crossOrigin: 'anonymous',
                hasBorders: false,
                hasControls: false,
                scaleX: scale,
                scaleY: scale,
                top: top,
                left: left,
                lockMovementX: !canEdit,
                lockMovementY: !canEdit,
                hoverCursor: !canEdit ? 'default' : 'move'
            });
            this.canvas.add(this.canvasImage);
            this.setCanvasZoom();
            if( canEdit ) {
                this.canvas.setActiveObject(this.canvasImage);
                this.canvasImage.on('moving', this.canvasImageMoveRef);
                this.canvasImage.on('mousewheel', this.canvasImageMousewheelRef);
            }
        }
        imageEl.onerror = () => {
            this.canvasError = 'Error loading image.';
        }
        imageEl.crossOrigin = 'Anonymous';
        imageEl.src = this.image.url + '?b=' + this.lastBrightness + '&c=' + this.lastContrast;
    }
    /*
    *   after render set the crop value
    *   run once and then remove
    */
    canvasAfterRender() {
        this.loading = false;
        this.canvas.off('after:render', this.canvasAfterRenderRef);
    }
    /*
    *   don't allow cropbox to move outside of canvas
    */
    canvasImageMove(e) {
        const canvasImage = this.canvasImage;
        const canvas = this.canvas;
        const imageWidth = canvasImage.width * canvasImage.scaleX;
        const imageHeight = canvasImage.height * canvasImage.scaleY;
        const x = canvasImage.left;
        const y = canvasImage.top;
        const maxY = canvas.height - imageHeight;
        const maxX = canvas.width - imageWidth;
    
        if( x < maxX ) {
            canvasImage.left = maxX;
        }
        if( y < maxY ) {
            canvasImage.top = maxY;
        }

        if( x > 0 ) {
            canvasImage.left = 0;
        }
        if( y > 0 ) {
            canvasImage.top = 0;
        }
        this.setCrop();
    }
    /*
    *   zoom image in/out
    */
    canvasImageMousewheel(e) {
        const delta = e.e.wheelDelta;
        e.e.preventDefault();
        const canvasImage = this.canvasImage;
        const canvas = this.canvas;
        const canvasWidth = canvas.width;
        const canvasHeight = canvas.height;
        const scale = canvasImage.scaleX;
        const imageWidth = canvasImage.width * scale;
        const imageHeight = canvasImage.height * scale;
        const step = .9;
        let newScale = scale * step;
        if( delta < 0 ) {
            newScale = scale / step
        }
        newScale = Math.max(this.minScale, newScale);
        newScale = Math.min(newScale, 5); // don't scale more than 5x the image
        canvasImage.scaleX = newScale;
        canvasImage.scaleY = newScale;

        const newWidth = canvasImage.width * newScale;
        const newHeight = canvasImage.height * newScale;
        const diffX = imageWidth - newWidth;
        const diffY = imageHeight - newHeight;
        const x = canvasImage.left;
        const y = canvasImage.top;
        let newX = x + (diffX / 2);
        let newY = y + (diffY / 2);
        const maxX = canvasWidth - newWidth;
        const maxY = canvasHeight - newHeight;
        newX = Math.min(0, newX);
        newY = Math.min(0, newY);
        if( newX < maxX ) {
            newX = maxX;
        }
        if( newY < maxY ) {
            newY = maxY;
        }
        canvasImage.left = newX;
        canvasImage.top = newY;        
        this.canvas.requestRenderAll();
        this.setCrop();
    }
    /*
    *   keep focus on image
    */
    canvasMouseDown() {
        this.canvas.setActiveObject(this.canvasImage);
    }
    /*
    *   set crop
    */
    setCrop() {
        const canvas = this.canvas;
        const canvasImage = this.canvasImage;
        const zoom = canvasImage.scaleX;
        const image = this.getImage();
        image.crop = {
            b: this.lastBrightness,
            c: this.lastContrast,
            width: canvas.width,
            height: canvas.height,
            top: Math.round(canvasImage.top) * -1,
            left: Math.round(canvasImage.left) * -1,
            zoom: zoom
        }
        image.urlWithParams = this.image.url + this.image.buildHttpParams(image.crop);
        if( this.size.value == 'full' ) {
            this.image.crop = image.crop;
            this.image.urlWithParams = image.urlWithParams;
        }
        this.imageService.refreshImage(this.image.id, image.urlWithParams, this.size.value);
        this.imageService.imagesChange.emit(this.imageService.images);
        const newCrop = JSON.stringify(image.crop);
        const initialCrop = this.initialCrop ? JSON.stringify(this.initialCrop) : '';
        this.cropped = false;
        if( newCrop !== initialCrop ) {
            this.cropped = true;
        }
    }
    /*
    *   reload canvas image
    */
    reloadCanvasImage() {
        this.loading = true;
        const url = this.image.url + '?b=' + this.lastBrightness + '&c=' + this.lastContrast;
        this.canvasImage.setSrc(url, img => {
            this.loading = false;
            this.canvas.requestRenderAll();            
        });
    }
    /*
    *   adjust brightness contrast
    */
    filterImage(filterObj) {
        if( !this.canvasImage ) {
            return;
        }
        const brightness = Math.round(filterObj.brightness * 100);
        const contrast = Math.round(filterObj.contrast * 100);
        this.lastBrightness = brightness;
        this.lastContrast = contrast;
        this.setCrop();
        this.reloadCanvasImage();
    }
    /*
    *   reset to saved crop
    */
    restoreInitialCrop() {
        const initialCrop = this.initialCrop;
        const image = this.getImage();
        const size = this.size.value;
        if( initialCrop ) {
            image.crop = initialCrop;
            image.urlWithParams = this.image.url + this.image.buildHttpParams(initialCrop);
            if( size == 'full' ) {
                this.image.crop = this.initialCrop;
                this.image.urlWithParams = this.image.url + this.image.buildHttpParams(initialCrop);
            }
        } else {
            delete image.crop;
            image.urlWithParams = this.image.url + this.image.buildHttpParams({});
            if( size == 'full' ) {
                delete this.image.crop;
                this.image.urlWithParams = this.image.url + this.image.buildHttpParams({});
            }
        }
        this.cropped = false;
        this.canvas.dispose();
        this.initCanvas();
    }
    /*
    *   zoom image in/out
    */
    zoom(out) {
        this.canvasImageMousewheel({
            e: {
                wheelDelta: out ? -1 : 1,
                preventDefault: () => {}
            }
        });
    }
    ngOnDestroy() {
        if( this.canvas ) {
            this.canvas.dispose();
        }
    }
}


// WEBPACK FOOTER //
// /opt/atlassian/pipelines/agent/build/src/swarm/src/app/slideshow/images/cropper.component.ts